import React, { useCallback, useMemo } from 'react';
import {
  OneOfSelectableProductListElements,
  type ProductID,
} from '@shared/models/product.model';
import {
  getProductCategoryElementFromSelection,
  getProductElementOwners,
  getProductElements,
  getProductListElementId,
} from '@features/products/product-list.helpers';
import { resolveProductElementsForActiveProposal } from '@features/products/product-list.helpers';
import { useResolvedVersion } from '@client/hooks/use-resolved-version.hooks';
import { SidePanel } from '@ui/layout';
import { ProductBulkEditForm } from '@features/products/ProductBulkEditForm';
import { DotLoader } from '@ui/DotLoader';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  isProductListCategoryGroup,
  isProductListGroup,
  isProductListItem,
} from '@shared/helpers/recursive-element.helpers';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { useUIState } from '@client/store/ui';
import { Page } from '@client/store/ui/ui.model';
import {
  expandElements,
  setElementExpanded,
} from '@client/hooks/use-expand-elements.hooks';
import { useGroupByCategory } from '@features/products/group-by-category.store';

const useStyles = makeStyles()((theme) => ({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: theme.spacing(10),
  },
  selectionSummary: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  selectionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  selectionItem: {
    fontSize: theme.typography.pxToRem(14),
  },
}));

type ProductElementProps = {
  element?: OneOfSelectableProductListElements;
};

export const ProductSidePanelContent: React.FC<ProductElementProps> = ({
  element,
}) => {
  const { classes } = useStyles();
  const navigateTo = useProjectNavigation();
  const { selectedPage } = useUIState('selectedPage');
  const groupByCategory = useGroupByCategory();
  const baselineProductElements = useMemo(
    () => getProductElements(element),
    [element],
  );
  const resolvedVersion = useResolvedVersion();
  const productElements = useMemo(
    () =>
      resolveProductElementsForActiveProposal(
        baselineProductElements,
        resolvedVersion,
      ),
    [baselineProductElements, resolvedVersion],
  );
  const owningElements = useMemo(
    () => (element ? getProductElementOwners(element) : []),
    [element],
  );

  const productCategoryElement = useMemo(
    () =>
      element ? getProductCategoryElementFromSelection(element) : undefined,
    [element],
  );
  const disableEpdSelector = useMemo(
    () => (element ? isProductListCategoryGroup(element) : false),
    [element],
  );
  const handleEpdSelect = useCallback(
    (id: ProductID, genericId?: ProductID) => {
      if (selectedPage !== Page.Products) return;
      if (!element) return;
      if (isProductListCategoryGroup(element)) return;
      if (isProductListItem(element)) {
        const resolvedGenericId =
          genericId ?? element.generic_product_id ?? element.product_id;
        const groupId = getProductListElementId(id, resolvedGenericId);
        if (groupByCategory && productCategoryElement?.category_id) {
          expandElements(productCategoryElement.category_id, groupId);
        } else {
          setElementExpanded(groupId, true);
        }
        navigateTo({ elementId: element.id });
        return;
      }
      if (isProductListGroup(element)) {
        const resolvedGenericId = genericId ?? element.generic_product_id;
        if (!resolvedGenericId) return;
        const elementId = getProductListElementId(id, resolvedGenericId);
        if (groupByCategory && productCategoryElement?.category_id) {
          expandElements(productCategoryElement.category_id, elementId);
        }
        navigateTo({ elementId });
      }
    },
    [
      element,
      groupByCategory,
      navigateTo,
      productCategoryElement?.category_id,
      selectedPage,
    ],
  );

  if (!element) {
    return (
      <Box className={classes.loadingContainer}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          Loading content <DotLoader />
        </Typography>
      </Box>
    );
  }

  return (
    <SidePanel.Content>
      <SidePanel.Content.Divider />
      <ProductBulkEditForm
        elements={owningElements}
        productCategoryElement={productCategoryElement}
        productElements={productElements}
        onEpdSelect={handleEpdSelect}
        disableEpdSelector={disableEpdSelector}
      />
      <SidePanel.Content.Divider />
    </SidePanel.Content>
  );
};
